import { userService } from "./user.service";

// Визитка пользователя: QR и текст vCard. Используется и в настройках кабинета
// (SettingsPage, вкладка «Личный кабинет»), и в режиме «визитка» (CardApp/VCardTab).
export const vcardService = {
  getVcardQr,
  downloadVcf,
  buildVcfDataUrl,
  parseVcardPreview,
  withNote,
};

// GET api/user/vcard-qr → { vcard: "BEGIN:VCARD…", qr: "data:image/png;base64,…" }
// Строится на лету из текущей анкеты, поэтому ответ не самодостаточен: после
// сохранения анкеты ручку нужно перезапросить (CardApp/VCardTab.closeUserInfoDialog).
// Режим «визитка» при этом держит last-known-good ответ в localStorage
// (CardApp/offlineCache) — read-through: сохранённое показывается, только пока не
// пришёл свежий ответ или если связи нет вовсе. Онлайн-поведение не меняется.
function getVcardQr(onSuccess, onError) {
  userService.getWrapper(
    "api/user/vcard-qr",
    (data) => onSuccess(data.data),
    onError
  );
}

// Android WebView помечает себя «; wv» в user-agent. Отличать нужно из-за скачивания:
// см. downloadVcf.
function isAndroidWebView() {
  return / wv\)/.test(navigator.userAgent);
}

function downloadVcf(vcard, filename = "contact.vcf") {
  if (!vcard) return;

  const a = document.createElement("a");

  // В обычном браузере — blob: он не раздувает разметку и точно уважает
  // атрибут download. В WebView мобильного приложения blob-ссылка до нативного
  // слоя не доходит вовсе (DownloadListener её не видит), и нажатие не делало
  // ничего — там нужен data:, его обёртка сохраняет сама, тем же путём, что и QR.
  const url = isAndroidWebView()
    ? buildVcfDataUrl(vcard)
    : URL.createObjectURL(new Blob([vcard], { type: "text/vcard" }));

  a.href = url;
  a.download = filename;
  a.click();

  if (!isAndroidWebView()) URL.revokeObjectURL(url);
}

// Для передачи файла нативной обёртке: она не умеет читать blob:-ссылки чужого контекста.
function buildVcfDataUrl(vcard) {
  if (!vcard) return null;
  return "data:text/vcard;charset=utf-8," + encodeURIComponent(vcard);
}

function unescapeValue(value) {
  return String(value)
    .replace(/\\n/gi, "\n")
    .replace(/\\([,;\\])/g, "$1");
}

// Обратная к unescapeValue: обратный слеш первым, иначе следом экранируются его же
// добавленные слеши.
function escapeValue(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/([,;])/g, "\\$1")
    .replace(/\r\n|\r|\n/g, "\\n");
}

// Имя свойства строки vCard: «item1.TEL;TYPE=CELL:+7…» → «TEL».
function propertyName(line) {
  const sep = line.indexOf(":");
  if (sep < 0) return "";

  const head = line.slice(0, sep).split(";")[0];
  const dot = head.lastIndexOf(".");

  return (dot < 0 ? head : head.slice(dot + 1)).trim().toUpperCase();
}

// Вырезает из визитки свойства NOTE (вместе со свёрнутыми продолжениями — они
// начинаются с пробела или таба) и возвращает остальные строки плюс собранный
// текст заметки.
function takeOutNote(vcard) {
  const lines = String(vcard).split(/\r\n|\r|\n/);
  const kept = [];
  const notes = [];
  let inNote = false;

  lines.forEach((line) => {
    const isFolded = /^[ \t]/.test(line);

    if (isFolded && inNote) {
      notes[notes.length - 1] += line.slice(1);
      return;
    }

    if (!isFolded) inNote = propertyName(line) === "NOTE";

    if (inNote) notes.push(line.slice(line.indexOf(":") + 1));
    else kept.push(line);
  });

  return { lines: kept, note: notes.map(unescapeValue).join("\n") };
}

// Подставляет в визитку комментарий пользователя — тот попадёт в поле «Заметки»
// карточки, которую предлагает создать системная форма добавления контакта
// (Android; на iOS библиотека обёртки это поле отбрасывает, см. CardApp/CLAUDE.md).
//
// Своей визитки это не касается: она строится бэкендом и уходит другим людям.
// Здесь — только чужая, уже отсканированная, и только в момент передачи её
// системной форме; на сервере и в локальном хранилище визитка остаётся исходной.
//
// keepOriginalNote — для визиток людей со стороны: их NOTE написан автором визитки,
// терять его нельзя, комментарий встаёт перед ним. У визитки пользователя NaviConf
// в NOTE лежит подпись платформы (VCardBuilder), и её комментарий вытесняет.
//
// Заметка кладётся одной строкой: в vCard у NOTE кардинальность *1, а веб-фолбэк
// downloadVcf отдаёт файл системе. Сворачивать длинную строку не пытаемся —
// разбирают её и без этого, а неверная точка переноса стоила бы дороже.
function withNote(vcard, note, keepOriginalNote) {
  const comment = String(note || "").trim();
  if (!vcard || !comment) return vcard;

  const eol = vcard.indexOf("\r\n") >= 0 ? "\r\n" : "\n";
  const { lines, note: original } = takeOutNote(vcard);
  const text = keepOriginalNote && original ? comment + "\n" + original : comment;

  const end = lines.map((line) => propertyName(line)).lastIndexOf("END");
  const at = end < 0 ? lines.length : end;
  lines.splice(at, 0, "NOTE:" + escapeValue(text));

  return lines.join(eol);
}

// Делит значение на компоненты по неэкранированным «;» (ORG:Компания;Отдел).
function splitComponents(value) {
  const parts = [];
  let current = "";
  for (let i = 0; i < value.length; i++) {
    const ch = value[i];
    if (ch === "\\" && i + 1 < value.length) {
      current += ch + value[i + 1];
      i++;
    } else if (ch === ";") {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  parts.push(current);
  return parts;
}

// Что именно зашито в QR — из самого текста vCard, а не из анкеты: коды полей анкеты
// (firstname/position/company…) живут на бэке, фронт их не знает.
// Возвращает { fullName, position, company, department } — любое поле может отсутствовать.
function parseVcardPreview(vcard) {
  const result = {};
  if (!vcard) return result;

  // Разворачиваем перенос строк vCard: продолжение начинается с пробела или таба.
  const unfolded = String(vcard)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n[ \t]/g, "");

  unfolded.split("\n").forEach((line) => {
    const sep = line.indexOf(":");
    if (sep < 0) return;
    const name = line.slice(0, sep).split(";")[0].toUpperCase();
    const value = line.slice(sep + 1);

    if (name === "FN") result.fullName = unescapeValue(value);
    else if (name === "TITLE") result.position = unescapeValue(value);
    else if (name === "ORG") {
      const parts = splitComponents(value);
      if (parts[0]) result.company = unescapeValue(parts[0]);
      if (parts[1]) result.department = unescapeValue(parts[1]);
    }
  });

  return result;
}
